import { BreakPriceDto, ProductModel } from "@insite/client-framework/Types/ApiModels";

/**
 * The back end always reports the first column of a price matrix as a quantity break of 1, whatever
 * break qty the ERP sent for that column (Insite's PricingServiceGeneric.GetMatrixBrackets). A product
 * with a minimum order quantity can never be bought at 1, so its break table should start at the
 * minimum: the break that prices the minimum quantity is shown at the minimum, and the breaks below it
 * are dropped.
 */

/**
 * The product's minimum order quantity in the unit of measure the prices are for, converted the same
 * way the default quantity ordered is (client-framework/Common/ProductInfo.createFromProduct).
 */
export const getMinimumOrderQty = (
    product: Pick<ProductModel, "minimumOrderQty" | "unitOfMeasures"> | undefined,
    unitOfMeasure?: string | null,
): number => {
    const minimumOrderQty = product?.minimumOrderQty ?? 0;
    const uom = unitOfMeasure ? product?.unitOfMeasures?.find(o => o.unitOfMeasure === unitOfMeasure) : undefined;
    return uom && uom.qtyPerBaseUnitOfMeasure > 0
        ? Math.ceil(minimumOrderQty / uom.qtyPerBaseUnitOfMeasure)
        : minimumOrderQty;
};

/** Break prices as they should be displayed for a product with the given minimum order quantity. */
export const applyMinimumOrderQty = <T extends Pick<BreakPriceDto, "breakQty">>(
    breakPrices: T[] | null | undefined,
    minimumOrderQty: number,
): T[] => {
    if (!breakPrices) {
        return [];
    }

    // Breaks arrive in ascending order, so the last one at or below the minimum is the price at the minimum.
    let minimumBreakIndex = -1;
    breakPrices.forEach((breakPrice, index) => {
        if (breakPrice.breakQty <= minimumOrderQty) {
            minimumBreakIndex = index;
        }
    });

    if (minimumOrderQty <= 1 || minimumBreakIndex === -1) {
        return breakPrices;
    }

    return [
        { ...breakPrices[minimumBreakIndex], breakQty: minimumOrderQty },
        ...breakPrices.slice(minimumBreakIndex + 1),
    ];
};

/** Shorthand for a product's break prices with its minimum order quantity applied. */
export const getDisplayBreakPrices = <T extends Pick<BreakPriceDto, "breakQty">>(
    breakPrices: T[] | null | undefined,
    product: Pick<ProductModel, "minimumOrderQty" | "unitOfMeasures"> | undefined,
    unitOfMeasure?: string | null,
): T[] => applyMinimumOrderQty(breakPrices, getMinimumOrderQty(product, unitOfMeasure));
